import { z } from 'zod';
/**
 * Forms, form templates, and the public per-attendee form surface.
 *
 * Deliberately duplicated from `backend/src/schemas/forms.ts` rather than shared
 * with it: `perf.yml` and `zap.yml` build the backend WITHOUT building this
 * package, so `backend/src` must not import `@event67/shared`. The two copies
 * are pinned together by a case in `backend/tests/contracts.test.ts` — bump one
 * without the other and that test fails with a precise diff.
 */
export const FORM_FIELD_TYPES = [
    'short_text',
    'long_text',
    'email',
    'phone',
    'number',
    'date',
    'single_select',
    'multi_select',
    'checkbox',
    'rating',
];
export const FormFieldTypeSchema = z.enum(FORM_FIELD_TYPES);
export const FORM_STATUSES = ['draft', 'open', 'closed'];
export const FormStatusSchema = z.enum(FORM_STATUSES);
/**
 * The token a preview or test-send puts in `{{ form_link }}` when the person
 * being rendered has no real link.
 *
 * A literal word, and that is the whole point. It used to be a plausible-looking
 * string on `example.com`, which made a sample link fail in the two worst ways
 * at once: an admin who clicked it landed on IANA's placeholder page with no
 * explanation, and an admin who forwarded it handed somebody a URL that looks
 * exactly like the real thing. `sample` is recognisable on sight, and the
 * attendee form page matches on it to explain itself instead of showing the
 * generic "we couldn't find that form".
 *
 * Mirrored in `backend/src/domain/business_process/links/form_link_bp.ts` and
 * pinned to this copy by `backend/tests/contracts.test.ts`. It is deliberately
 * NOT a token the server knows anything about: `/v1/public/forms/sample` stays
 * an ordinary uniform 404, because a server that answered it specially would be
 * confirming, to anyone who asked, that this exact string is meaningful.
 */
export const SAMPLE_FORM_TOKEN = 'sample';
/**
 * Published so the admin builder can enforce the same caps the server does,
 * under the control rather than as a raw ZodError after the fact. Pinned to the
 * backend's copies by `backend/tests/contracts.test.ts` — a cap that drifts
 * between the two is a limit the console never mentions and the server refuses.
 */
export const MAX_OPTIONS_PER_FIELD = 100;
export const MAX_OPTION_TEXT_LENGTH = 200;
/**
 * How many choices a field may carry before its options stop being shown all at
 * once — the one number that decides whether a choice field is drawn as a list
 * of options or as a dropdown.
 *
 * It lives here because **two surfaces have to agree on it or the product lies
 * to the organiser**: the admin's live preview draws what the attendee will see,
 * and the attendee page draws what they actually see. Those were two independent
 * decisions until this constant existed — the catalog said a `single_select`
 * previews as `choice` (option circles) while the schema compiler emitted
 * `ui:widget: 'select'` (a native dropdown), so every single-choice field
 * previewed as radio buttons and shipped as a dropdown. Nothing failed; the
 * preview was simply not a preview.
 *
 * Six, because a list of options is better than a dropdown exactly while the
 * whole question fits on the screen it is read on. Six rows of a touch-sized
 * control is around 300px — half a phone viewport, leaving the label above and
 * the next field below in view. Past that the list is a scroll of its own and a
 * dropdown is the kinder control.
 *
 * Both sides pin this to BEHAVIOUR rather than to each other
 * (`docs/testing-discipline.md`): each asserts that exactly this many options
 * draws a list and one more draws a dropdown, so bumping the number without
 * moving the renderer fails on both sides instead of staying quietly green.
 */
export const MAX_OPTIONS_SHOWN_AT_ONCE = 6;
/** The per-property text caps the served catalog publishes as `maxLength`. */
export const MAX_FIELD_KEY_LENGTH = 40;
export const MAX_FIELD_LABEL_LENGTH = 200;
export const MAX_FIELD_HELP_LENGTH = 500;
export const MAX_FIELD_PLACEHOLDER_LENGTH = 200;
/**
 * What counts as a phone number — permissive on purpose, and not E.164.
 *
 * Attendees type their own number the way they always have, so the rule is only
 * "could this plausibly be one": the separators people actually use, at least
 * one digit, and a length that rules out both a typo and a paragraph.
 *
 * The attendee page validates against these client-side before submitting, so
 * they must equal the backend's copy exactly — `backend/tests/contracts.test.ts`
 * pins them. A drift is a number the page accepts and the server then refuses.
 */
export const MIN_PHONE_LENGTH = 7;
export const MAX_PHONE_LENGTH = 20;
/** Anchored, because a JSON Schema `pattern` is an unanchored search. */
export const PHONE_PATTERN = '^(?=.*[0-9])[0-9+() -]+$';
/**
 * What an attendee is told when their number does not match. The attendee page
 * shows this before submitting and the server shows its own copy afterwards, so
 * the two are pinned equal — otherwise the same answer is refused twice in two
 * different sentences.
 */
export const PHONE_FORMAT_MESSAGE = 'Enter a phone number, using only digits, spaces and + - ( ).';
export const FormFieldOptionSchema = z
    .object({
    value: z.string().min(1).max(MAX_OPTION_TEXT_LENGTH),
    label: z.string().min(1).max(MAX_OPTION_TEXT_LENGTH),
})
    .strict();
/**
 * The field DSL. `key` becomes a JSON Schema property name and a CSV column
 * header, so it is narrow and meant to be stable — renaming one orphans every
 * answer already stored under the old name.
 *
 * The `^[a-z][a-z0-9_]{0,39}$` rule and uniqueness are enforced by the SERVER
 * rather than here, so a bad key comes back as a per-field error the builder can
 * attach to a control instead of a raw ZodError.
 */
export const FormFieldSchema = z
    .object({
    key: z.string().min(1).max(MAX_FIELD_KEY_LENGTH),
    type: FormFieldTypeSchema,
    label: z.string().min(1).max(MAX_FIELD_LABEL_LENGTH),
    help: z.string().max(MAX_FIELD_HELP_LENGTH).optional(),
    required: z.boolean(),
    placeholder: z.string().max(MAX_FIELD_PLACEHOLDER_LENGTH).optional(),
    options: z.array(FormFieldOptionSchema).max(MAX_OPTIONS_PER_FIELD).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    maxLength: z.number().int().optional(),
})
    .strict();
export const MAX_FIELDS_PER_FORM = 100;
export const FormFieldsSchema = z.array(FormFieldSchema).max(MAX_FIELDS_PER_FORM);
/**
 * The served field-type catalog. The admin field builder renders 100% from this
 * and hardcodes no field type, so adding a type is a backend-only change.
 * Shaped to match the email block catalog.
 */
export const FIELD_PROP_CONTROLS = [
    'text',
    'textarea',
    'url',
    'select',
    'number',
    'checkbox',
    'options',
];
export const FormFieldPropSpecSchema = z.object({
    key: z.string(),
    label: z.string(),
    control: z.enum(FIELD_PROP_CONTROLS),
    required: z.boolean(),
    /**
     * Cap on the PROPERTY's own text length (a label is at most 200 characters).
     * For `control: 'options'` it caps each option's value and label.
     */
    maxLength: z.number().optional(),
    /**
     * Cap on how many ENTRIES the property may hold — `control: 'options'` only.
     * Distinct from `maxLength` because "length" on a list is ambiguous between
     * the entry count and the text of each entry.
     */
    maxItems: z.number().optional(),
    /** For `control: 'number'` — the range the PROPERTY's value may take. */
    minValue: z.number().optional(),
    maxValue: z.number().optional(),
    options: z.array(FormFieldOptionSchema).optional(),
    hint: z.string().optional(),
});
/**
 * Which attendee-facing control a type draws as, so the admin preview does not
 * have to infer it. Optional and typed as a plain string on purpose: a consumer
 * that meets a control it does not draw should fall back to a text box, never
 * fail to parse the catalog — the whole point of serving it is that a tenth
 * field type can ship without a console deploy.
 */
export const FormFieldTypeSpecSchema = z.object({
    type: FormFieldTypeSchema,
    label: z.string(),
    description: z.string(),
    supportsOptions: z.boolean(),
    preview: z.object({ control: z.string() }).optional(),
    fields: z.array(FormFieldPropSpecSchema),
});
// --- Responses -------------------------------------------------------------
/**
 * `jsonSchema`/`uiSchema` are deliberately absent: `fields` is the source of
 * truth and the admin preview renders from it. RJSF is confined to the public
 * form bundle (ADR-0006), so the console has nothing to do with a compiled
 * schema.
 */
export const FormDtoSchema = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    name: z.string(),
    description: z.string(),
    status: FormStatusSchema,
    fields: FormFieldsSchema,
    schemaVersion: z.number().int().positive(),
    /**
     * Optimistic-concurrency token. Echo it back on a document PATCH; the server
     * refuses with `details.reason: 'concurrent_edit'` when it has moved on.
     *
     * Bumped by a document edit (name, description, fields) and by NOTHING else —
     * a status change deliberately leaves it alone, so a colleague opening or
     * closing the form while you edit is not a conflict.
     *
     * Not the same as `schemaVersion`, which moves only when the compiled schema
     * changes and so would not catch two concurrent renames.
     */
    concurrentVersion: z.number().int().positive(),
    submissionCount: z.number().int().nonnegative(),
    compiledAt: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const FormTemplateDtoSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().uuid(),
    name: z.string(),
    fields: FormFieldsSchema,
    /** Same optimistic-concurrency contract as FormDto.concurrentVersion. */
    concurrentVersion: z.number().int().positive(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const FormSubmissionDtoSchema = z.object({
    id: z.string().uuid(),
    formId: z.string().uuid(),
    attendeeId: z.string().uuid(),
    attendeeName: z.string(),
    attendeeEmail: z.string(),
    schemaVersion: z.number().int().positive(),
    answers: z.record(z.unknown()),
    revision: z.number().int().positive(),
    submittedAt: z.string(),
    updatedAt: z.string(),
});
/**
 * What `GET /v1/public/forms/:token` returns to an unauthenticated caller.
 *
 * `recipient` carries a FIRST NAME AND NOTHING ELSE — enough to say "Hello
 * Alex". No email, no company, no attendee id, no event id, no form id: the
 * response is a function of the token and nothing an attacker holding it did not
 * already receive by email.
 */
export const PublicFormViewSchema = z.object({
    form: z.object({
        name: z.string(),
        description: z.string(),
        jsonSchema: z.record(z.unknown()),
        uiSchema: z.record(z.unknown()),
        schemaVersion: z.number().int().positive(),
    }),
    event: z.object({ name: z.string(), endAt: z.string() }),
    theme: z.record(z.unknown()),
    recipient: z.object({ firstName: z.string() }),
    submission: z
        .object({
        answers: z.record(z.unknown()),
        revision: z.number().int().positive(),
        updatedAt: z.string(),
    })
        .nullable(),
});
// --- Requests --------------------------------------------------------------
export const CreateFormRequestSchema = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    fields: FormFieldsSchema,
});
/**
 * `concurrentVersion` is REQUIRED. An optional token degrades silently to
 * last-write-wins the moment a client forgets it, which is the bug it exists to
 * close.
 */
export const PatchFormRequestSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).optional(),
    fields: FormFieldsSchema.optional(),
    concurrentVersion: z.number().int().positive(),
});
export const SetFormStatusRequestSchema = z.object({ status: FormStatusSchema });
export const CreateFormFromTemplateRequestSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).optional(),
});
export const CreateFormTemplateRequestSchema = z.object({
    name: z.string().min(1).max(200),
    fields: FormFieldsSchema,
});
export const PatchFormTemplateRequestSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    fields: FormFieldsSchema.optional(),
    concurrentVersion: z.number().int().positive(),
});
/**
 * The public submission body. Answers are validated server-side against the
 * form's STORED compiled schema, not this — `z.record` only asserts that a
 * JSON object arrived.
 */
export const SubmitFormAnswersRequestSchema = z.object({
    answers: z.record(z.unknown()),
});
//# sourceMappingURL=forms.js.map